import React, { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { useAddon } from '../../hooks/useAddon';
import { ADDONS } from '../../utils/addons';
import Tooltip from '../common/Tooltip';
import TaskModal from '../forms/TaskModal';
import EmailComposer from '../forms/EmailComposer';
import {
  evaluateQualification,
  buildQualificationEntry,
  buildQualificationTaskPrefill,
  buildQualificationEmailDraft,
  QUALIFICATION_ANSWER_MAX
} from '../../utils/opportunityQualification';
import { saveQualificationAnswer, createQualificationTask } from '../../utils/opportunityQualificationActions';

const STATUS_META = {
  confirmed: { label: 'Confirmed', className: 'bg-green-100 text-green-800' },
  unknown: { label: 'Not known yet', className: 'bg-yellow-100 text-yellow-800' },
  open: { label: 'Not asked', className: 'bg-gray-100 text-gray-700' }
};

/**
 * Guided opportunity qualification (AI Sales Agent Phase 4 increment 2, rules only).
 * Hidden unless the aiSalesAgent add-on and the owner-controlled `qualificationEnabled` flag are on.
 */
const QualificationCard = ({
  opportunity, currentUser, canEdit = false,
  users = [], canAssignAny = false, formatters, onUpdated, onToast
}) => {
  const { enabled: addonEnabled } = useAddon(ADDONS.AI_SALES_AGENT);
  const [flagEnabled, setFlagEnabled] = useState(false);
  const [editor, setEditor] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [taskKey, setTaskKey] = useState(null);
  const [emailDraft, setEmailDraft] = useState(null);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, collectionPath('config'), 'aiAgentSettings'),
      snap => setFlagEnabled(snap.data()?.qualificationEnabled === true),
      err => {
        console.error('Failed to load qualification settings:', err);
        setFlagEnabled(false);
      }
    );
    return unsubscribe;
  }, []);

  const result = useMemo(() => evaluateQualification(opportunity || {}, formatters), [opportunity, formatters]);
  const taskPrefill = useMemo(
    () => (taskKey ? buildQualificationTaskPrefill(opportunity, taskKey, currentUser) : null),
    [taskKey, opportunity, currentUser]
  );

  if (!opportunity?.id || !addonEnabled || !flagEnabled) return null;

  const toast = (message, type = 'success') => onToast?.({ message, type });
  const startEdit = item => {
    setError('');
    setReviewing(false);
    setEditor({ key: item.key, label: item.label, question: item.question, before: item,
      status: item.status === 'unknown' ? 'unknown' : 'confirmed', answer: item.answer });
  };
  const review = () => {
    try {
      buildQualificationEntry({ status: editor.status, answer: editor.answer },
        { authUid: auth.currentUser?.uid || currentUser?.uid, user: currentUser });
      setError('');
      setReviewing(true);
    } catch (err) {
      setError(err.message);
    }
  };
  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const entry = await saveQualificationAnswer(opportunity, editor.key,
        { status: editor.status, answer: editor.answer }, currentUser);
      onUpdated?.(editor.key, entry);
      toast(`${editor.label} saved`);
      setEditor(null);
      setReviewing(false);
    } catch (err) {
      console.error('Saving qualification answer failed:', err);
      setError(`Could not save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const next = result.next;
  const hasEmail = Boolean(opportunity.contactEmail);

  return (
    <section aria-label="Opportunity qualification" className="border border-indigo-200 rounded-lg bg-white p-4 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-lg font-semibold text-gray-900">Qualification</h2>
        <span className="text-sm text-gray-600">
          {result.confirmedCount} of {result.total} confirmed · rules-based, no AI
        </span>
      </div>

      {result.stageHint && (
        <p role="status" className="mb-3 rounded bg-green-50 border border-green-200 p-2 text-sm text-green-800">
          All five answers are confirmed. Consider moving this opportunity to <strong>Qualified</strong>.
        </p>
      )}

      {next && !editor && (
        <div className="mb-3 rounded bg-indigo-50 border border-indigo-100 p-3">
          <p className="text-xs font-semibold uppercase text-indigo-700">Next question · {next.label}</p>
          <p className="text-sm text-gray-900 mt-1">{next.question}</p>
          {next.evidence && <p className="text-xs text-gray-600 mt-1">{next.evidence}</p>}
          {canEdit && (
            <div className="flex flex-wrap gap-2 mt-2">
              <Tooltip text="Record the customer's answer; you review it before saving">
                <button type="button" onClick={() => startEdit(next)}
                  className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700">Record answer</button>
              </Tooltip>
              <Tooltip text="Open a follow-up task prefilled with this question">
                <button type="button" onClick={() => setTaskKey(next.key)}
                  className="px-3 py-1 text-sm border border-indigo-600 text-indigo-700 rounded hover:bg-indigo-50">Create follow-up task</button>
              </Tooltip>
              <Tooltip text={hasEmail ? 'Open an email draft to the contact; nothing is sent until you press Send' : 'Add a contact email to this opportunity first'}>
                <button type="button" disabled={!hasEmail}
                  onClick={() => setEmailDraft(buildQualificationEmailDraft(opportunity, next.key, currentUser))}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Draft email</button>
              </Tooltip>
            </div>
          )}
          {canEdit && !hasEmail && (
            <p className="mt-2 text-xs text-gray-600">Add a contact email to this opportunity to draft an email.</p>
          )}
        </div>
      )}

      {editor && (
        <div className="mb-3 rounded border border-gray-300 p-3" aria-label={`Record ${editor.label}`} role="group">
          <p className="text-sm font-semibold">{editor.label}</p>
          <p className="text-sm text-gray-700 mb-2">{editor.question}</p>
          {!reviewing ? (
            <>
              <div className="flex flex-wrap gap-4 mb-2 text-sm">
                {['confirmed', 'unknown'].map(status => (
                  <label key={status} className="flex items-center gap-1">
                    <input type="radio" name="qualification-status" value={status} checked={editor.status === status}
                      onChange={() => setEditor(prev => ({ ...prev, status }))} />
                    {STATUS_META[status].label}
                  </label>
                ))}
              </div>
              <label className="block text-sm">Answer
                <textarea rows={3} maxLength={QUALIFICATION_ANSWER_MAX} value={editor.answer}
                  onChange={event => setEditor(prev => ({ ...prev, answer: event.target.value }))}
                  className="mt-1 w-full border rounded p-2" />
              </label>
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={review} className="px-3 py-1 text-sm bg-indigo-600 text-white rounded">Review</button>
                <button type="button" onClick={() => setEditor(null)} className="px-3 py-1 text-sm border rounded">Cancel</button>
              </div>
            </>
          ) : (
            <>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div className="min-w-0">
                  <dt className="font-medium text-gray-600">Before</dt>
                  <dd className="break-words">{editor.before.status === 'open' ? 'Not recorded'
                    : `${STATUS_META[editor.before.status].label}${editor.before.answer ? `: ${editor.before.answer}` : ''}`}</dd>
                </div>
                <div className="min-w-0">
                  <dt className="font-medium text-gray-600">After</dt>
                  <dd className="break-words">{`${STATUS_META[editor.status].label}${editor.answer.trim() ? `: ${editor.answer.trim()}` : ''}`}</dd>
                </div>
              </dl>
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={save} disabled={saving}
                  className="px-3 py-1 text-sm bg-green-600 text-white rounded disabled:opacity-50">{saving ? 'Saving…' : 'Confirm and save'}</button>
                <button type="button" onClick={() => setReviewing(false)} disabled={saving} className="px-3 py-1 text-sm border rounded">Back</button>
              </div>
            </>
          )}
          {error && <p role="alert" className="text-sm text-red-600 mt-2">{error}</p>}
        </div>
      )}

      <ul className="divide-y divide-gray-100">
        {result.items.map(item => (
          <li key={item.key} className="py-2 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">
                {item.label}{' '}
                <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${STATUS_META[item.status].className}`}>{STATUS_META[item.status].label}</span>
              </p>
              {item.answer
                ? <p className="text-sm text-gray-700 break-words">{item.answer}</p>
                : item.evidence && <p className="text-xs text-gray-500">{item.evidence}</p>}
              {item.updatedByName && (
                <p className="text-xs text-gray-400">
                  {item.updatedByName}{item.updatedAt ? ` · ${item.updatedAt.toLocaleDateString('en-US')}` : ''}
                </p>
              )}
            </div>
            {canEdit && !editor && (
              <Tooltip text={`${item.status === 'open' ? 'Record' : 'Update'} the ${item.label.toLowerCase()} answer`}>
                <button type="button" onClick={() => startEdit(item)}
                  className="text-sm text-indigo-600 hover:underline flex-shrink-0">{item.status === 'open' ? 'Record' : 'Update'}</button>
              </Tooltip>
            )}
          </li>
        ))}
      </ul>

      {taskKey && taskPrefill && (
        <TaskModal
          salesReps={users.filter(u => u.uid)}
          initial={taskPrefill}
          isAdmin={canAssignAny}
          customers={opportunity.customerId ? [{ id: opportunity.customerId, customerName: opportunity.customerName }] : []}
          opportunities={[{ id: opportunity.id, opportunityNumberLabel: opportunity.opportunityNumberLabel, customerName: opportunity.customerName }]}
          associatedOpportunityId={opportunity.id}
          onClose={() => setTaskKey(null)}
          onSave={async taskData => {
            try {
              const taskId = await createQualificationTask(opportunity, taskKey, taskData, currentUser);
              setTaskKey(null);
              toast(`Task ${taskId} created`);
            } catch (err) {
              console.error('Create qualification task failed:', err);
              toast(`Failed to create task: ${err.message}`, 'error');
              throw err;
            }
          }}
        />
      )}
      {emailDraft && (
        <EmailComposer
          initialDraft={emailDraft}
          onClose={() => setEmailDraft(null)}
          onSent={() => { setEmailDraft(null); toast('Email sent'); }}
          onError={msg => toast(msg, 'error')}
        />
      )}
    </section>
  );
};

export default QualificationCard;
